import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { addMinutes, differenceInCalendarDays, format } from 'date-fns'
import {
  AlertTriangle,
  CalendarDays,
  CalendarIcon,
  Check,
  Clock,
  Loader2,
  MapPin,
  Minus,
  Pencil,
  Plus,
  Trash2,
  Users,
} from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { toast } from 'sonner'
import { Link } from '@tanstack/react-router'

import type { Inquiry, InquiryEvent } from '@/gen/cortado/v1/inquiries_pb'
import type { JobEventLite } from '@/gen/cortado/v1/jobs_pb'
import { JobEventSourceEnum } from '@/gen/cortado/v1/settings_pb'
import { useCreateInquiryEvent, useUpdateInquiryEvent, useDeleteInquiryEvent } from '@/stores/leads.store'
import { useInquiryOverlaps } from '@/stores/overlaps.store'
import { dateToTimestamp, formatShortDate } from '@/lib/date-utils'
import {
  combineDateAndTime,
  formatTimestampToTimeString,
  MINUTE_IN_HOURS,
  roundToMinute,
  timestampToDate,
} from '@/lib/inquiry-utils'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import { formatTime12Hour } from '@/lib/time-utils'
import { ICON_SWAP_TRANSITION, usePendingWithMinimumVisibleDuration } from '@/hooks/use-pending-with-minimum-visible-duration'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { InputGroup } from '@/components/ui/input-group'
import { PlacesAutocomplete } from '@/components/ui/places-autocomplete'
import { AnimatedCollapse, CollapseChevron } from '@/components/shared/animated-collapse'
import { DatePicker } from '@/components/shared/date-picker'
import { TimePicker } from '@/components/shared/time-picker'
import { timezoneLabel } from '@/components/shared/timezone-select'
import { Badge } from '@/components/ui/badge'
import { getEventConflicts, type ConflictInfo } from '@/components/leads/id/lead-events-card'
import { splitAddressLines } from '@/components/pipeline/job-original-inquiry'
import { CardSummaryItems, type CardSummaryItem } from '@/components/pipeline/card-summary-items'
import {
  jobDetailEventLine,
  jobDetailEventStageLabel,
  jobDetailEventStageVariant,
  jobDetailEventTitle,
  hoursText,
  plural,
} from '@/components/pipeline/job-detail-view'

// Shared across every editable cell's read state so values line up with the
// rest of the page's established type scale.
const COLUMN_VALUE_CLASS = 'text-sm'
// Same caption style as the DATES/LOCATION labels on the Original Inquiry card.
const FIELD_LABEL_CLASS = 'font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground'

// px-1.5 (6px) both sides; -ml-1.5 pulls the content flush under the caption, hover bg keeps its 6px.
const PICKER_CELL_CLASS = '-ml-1.5 h-auto w-auto border-0 bg-transparent px-1.5 py-1 has-[>svg]:px-1.5 font-normal shadow-none hover:bg-muted'

interface JobEventsProps {
  /** The job the events belong to; each booked event opens its page under this job. */
  jobId?: string
  inquiryId?: string
  /** `ListJobEvents` rows for this job — read-only for a booked job. */
  events: JobEventLite[]
  isLoading: boolean
  inquiry: Inquiry | undefined
  isInquiryLoading: boolean
  /**
   * Initial collapsed state (default false). Only read on mount; the user's
   * own toggle wins afterwards. Collapsed, the card is its header row alone,
   * with a summary beside the title (one event's details, or several events'
   * dates) instead of the event blocks.
   */
  defaultCollapsed?: boolean
  /**
   * Quote phase: each requested event shows read-only until its pencil
   * unlocks it, and a hint points event changes for the client at the quote.
   * Only applies while the events are editable (before booking).
   */
  readFirst?: boolean
  /** The job's current quote, linked from the read-first hint. */
  quoteId?: string
}

/** "3 hours" / "1 hour" / "1h 15m" / "45m" / "-" for the Duration column. */
function formatDuration(value: number | undefined) {
  if (!value) return <span className="text-muted-foreground">-</span>
  const totalMinutes = Math.round(value * 60)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (minutes === 0) return <span>{hours} {hours === 1 ? 'hour' : 'hours'}</span>
  return <span>{hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`}</span>
}

/** "-" for an unset/zero count, matching the legacy table's "0 means not configured" convention. */
function formatCount(value: number | undefined) {
  if (value == null || value === 0) return <span className="text-muted-foreground">-</span>
  return <span>{value}</span>
}

const CELL_TRIGGER_CLASS = cn(
  '-ml-1.5 flex w-[calc(100%+0.375rem)] min-w-0 items-center gap-1.5 rounded px-1.5 py-1 text-left outline-none transition-colors',
  'hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring/50',
  'disabled:cursor-default disabled:opacity-60 disabled:hover:bg-transparent',
  COLUMN_VALUE_CLASS,
)

interface NumberCellProps {
  value: number | undefined
  ariaLabel: string
  disabled?: boolean
  min?: number
  step?: number
  /** When set, shows compact −/+ buttons that adjust by this amount. */
  stepBy?: number
  /** Min-width class for the stepper's value segment so it doesn't jump between rows. */
  valueClassName?: string
  format: (value: number | undefined) => ReactNode
  onCommit: (next: number) => void
}

const STEP_BUTTON_CLASS = 'h-full w-7 shrink-0 rounded-none text-muted-foreground'

/**
 * Click-or-Enter to edit, Enter/blur to commit, Escape to cancel. Mirrors the
 * job-title inline-editing convention on the pipeline job detail page
 * (suppressing the blur-commit after an explicit Escape cancel). Every commit,
 * stepper clicks included, goes straight to the event's batched save, which
 * shows it at once and debounces the request.
 */
function NumberCell({ value, ariaLabel, disabled, min = 0, step = 1, stepBy, valueClassName, format, onCommit }: NumberCellProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? 0)
  const suppressBlurRef = useRef(false)

  const current = value ?? 0
  function stepTo(next: number) {
    const clamped = Math.max(min, next)
    if (clamped !== current) onCommit(clamped)
  }

  function startEditing() {
    if (disabled) return
    setDraft(current)
    setIsEditing(true)
  }
  function commit() {
    setIsEditing(false)
    if (draft !== current) onCommit(draft)
  }
  function cancel() {
    suppressBlurRef.current = true
    setDraft(current)
    setIsEditing(false)
  }
  function handleBlur() {
    if (suppressBlurRef.current) {
      suppressBlurRef.current = false
      return
    }
    commit()
  }
  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      commit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancel()
    }
  }

  if (isEditing) {
    return (
      <Input
        autoFocus
        type="number"
        min={min}
        step={step}
        value={draft}
        aria-label={ariaLabel}
        onChange={(e) => setDraft(Math.max(min, Number(e.target.value) || 0))}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className="h-8 w-20"
      />
    )
  }

  const trigger = (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={startEditing}
      disabled={disabled}
      className={cn(
        CELL_TRIGGER_CLASS,
        stepBy != null && 'ml-0 w-auto flex-1 justify-center rounded-none',
        stepBy != null && valueClassName,
      )}
    >
      {format(value)}
    </button>
  )
  if (stepBy == null) return trigger

  return (
    <div className="flex h-8 w-fit items-center overflow-hidden rounded-md border border-input shadow-xs">
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className={cn(STEP_BUTTON_CLASS, 'border-r border-input')}
        aria-label={`Decrease ${ariaLabel.replace(/^Edit /, '')}`}
        disabled={disabled || current <= min}
        onClick={() => stepTo(current - stepBy)}
      >
        <Minus className="h-3 w-3" />
      </Button>
      {trigger}
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className={cn(STEP_BUTTON_CLASS, 'border-l border-input')}
        aria-label={`Increase ${ariaLabel.replace(/^Edit /, '')}`}
        disabled={disabled}
        onClick={() => stepTo(current + stepBy)}
      >
        <Plus className="h-3 w-3" />
      </Button>
    </div>
  )
}

interface LocationCellProps {
  value: string
  disabled?: boolean
  onCommit: (next: string) => void
}

function LocationCell({ value, disabled, onCommit }: LocationCellProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  // PlacesAutocomplete calls onBlur right after onChange when a suggestion is
  // picked, and again (delayed) after the input unmounts, so commit from refs
  // rather than the stale render closure and only once per edit session.
  const draftRef = useRef(value)
  const editingRef = useRef(false)

  function startEditing() {
    if (disabled) return
    draftRef.current = value
    editingRef.current = true
    setDraft(value)
    setIsEditing(true)
  }
  function updateDraft(next: string) {
    draftRef.current = next
    setDraft(next)
  }
  function commit() {
    if (!editingRef.current) return
    editingRef.current = false
    setIsEditing(false)
    const trimmed = draftRef.current.trim()
    if (trimmed !== value) onCommit(trimmed)
  }
  function cancel() {
    editingRef.current = false
    setIsEditing(false)
  }
  function handleBlur() {
    commit()
  }
  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      commit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancel()
    }
  }

  if (isEditing) {
    return (
      <InputGroup>
        <PlacesAutocomplete
          autoFocus
          value={draft}
          onChange={updateDraft}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          floatingDropdown
          placeholder="Enter event location"
        />
      </InputGroup>
    )
  }

  return (
    <button
      type="button"
      aria-label="Edit Location"
      onClick={startEditing}
      disabled={disabled}
      title={value || undefined}
      className={CELL_TRIGGER_CLASS}
    >
      {value ? <span className="truncate">{value}</span> : <span className="text-muted-foreground">-</span>}
    </button>
  )
}

interface DateTimeCellProps {
  event: InquiryEvent
  disabled?: boolean
  onCommit: (date: Date | undefined, time: string) => void
}

/** Time part of the event timestamp; needs a date, so it is disabled until one is set. */
function TimeCell({ event, disabled, onCommit }: DateTimeCellProps) {
  const date = timestampToDate(event.eventDate)
  return (
    <TimePicker
      value={date ? formatTimestampToTimeString(event.eventDate) : undefined}
      onChange={(time) => onCommit(date, time)}
      disabled={disabled || !date}
      placeholder="Add time"
      className={PICKER_CELL_CLASS}
    />
  )
}

/** Minutes past midnight of an "HH:mm" time. */
function minutesOfDay(time: string) {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

/** When a dated event ends (start + length), or undefined without a date or a length. */
function eventEndDate(event: Pick<InquiryEvent, 'eventDate' | 'eventLength'>): Date | undefined {
  const start = timestampToDate(event.eventDate)
  if (!start || !event.eventLength) return undefined
  return addMinutes(start, Math.round(event.eventLength * 60))
}

/**
 * The event length, in hours, that a picked end time implies: the exact whole
 * minutes from the start (at least 1), with an end before the start read as
 * the next day (overnight). An end equal to the start is undefined (no
 * change): it would mean 0 or 24 hours, and either is almost surely a slip
 * while clicking through the picker's columns.
 */
function eventLengthForEndTime(start: Date, endTime: string): number | undefined {
  let minutes = minutesOfDay(endTime) - (start.getHours() * 60 + start.getMinutes())
  if (minutes === 0) return undefined
  if (minutes < 0) minutes += 24 * 60
  return roundToMinute(Math.max(1, minutes) * MINUTE_IN_HOURS)
}

/** Whether a length in hours is the same whole number of minutes as a saved one. */
function sameMinuteLength(hours: number, saved: number | undefined) {
  return saved != null && Math.round(hours * 60) === Math.round(saved * 60)
}

/** "+1 day" / "+2 days" for an end on a later calendar day than the start. */
function dayOffsetLabel(days: number) {
  return `+${days} ${days === 1 ? 'day' : 'days'}`
}

function DayOffsetMarker({ days }: { days: number }) {
  if (days < 1) return null
  return <span className="ml-1 text-xs font-medium text-muted-foreground">{dayOffsetLabel(days)}</span>
}

/** The inferred length under the Start – End pair, e.g. "3.5 hours". */
function DurationCaption({ hours }: { hours: number | undefined }) {
  if (!hours) return null
  return <p className="text-xs text-muted-foreground">{formatDuration(hours)}</p>
}

/** "6:30 PM" for a Date, as the time picker shows it. */
function formatClockTime(date: Date) {
  return formatTime12Hour(format(date, 'HH:mm'))
}

interface StartEndCellProps {
  event: InquiryEvent
  /** Start time changed; the length is kept, so the end moves with it. */
  onStartCommit: (date: Date, time: string) => void
  /** End time changed; only the length changes. */
  onLengthCommit: (hours: number) => void
}

/**
 * Start and End pickers of a dated event. The data stays start + length:
 * picking an End saves the length it implies, in whole minutes, and the End
 * picker offers every minute, so any length can be picked.
 */
function StartEndCell({ event, onStartCommit, onLengthCommit }: StartEndCellProps) {
  const start = timestampToDate(event.eventDate)
  if (!start) return null
  const startTime = format(start, 'HH:mm')
  const end = eventEndDate(event)
  const endTime = end ? format(end, 'HH:mm') : undefined
  const days = end ? differenceInCalendarDays(end, start) : 0
  const endLabel = endTime
    ? `End time ${formatTime12Hour(endTime)}${days > 0 ? `, ${dayOffsetLabel(days)}` : ''}`
    : 'End time'

  return (
    <>
      <div className="flex items-center gap-1">
        <TimePicker
          value={startTime}
          onChange={(time) => onStartCommit(start, time)}
          aria-label={`Start time ${formatTime12Hour(startTime)}`}
          className={PICKER_CELL_CLASS}
        />
        <span aria-hidden="true" className="text-sm text-muted-foreground">
          –
        </span>
        <TimePicker
          value={endTime}
          onChange={(time) => {
            const next = eventLengthForEndTime(start, time)
            if (next != null && !sameMinuteLength(next, event.eventLength)) onLengthCommit(next)
          }}
          placeholder="Add end"
          aria-label={endLabel}
          suffix={<DayOffsetMarker days={days} />}
          className={cn(PICKER_CELL_CLASS, 'ml-0')}
        />
      </div>
      <DurationCaption hours={event.eventLength} />
    </>
  )
}

/** "8:16 PM – 11:46 PM" (+1 day when overnight), or just the start without a length; undefined when undated. */
function formatStartEndText(event: Pick<InquiryEvent, 'eventDate' | 'eventLength'>): string | undefined {
  const start = timestampToDate(event.eventDate)
  if (!start) return undefined
  const end = eventEndDate(event)
  if (!end) return formatClockTime(start)
  const days = differenceInCalendarDays(end, start)
  return `${formatClockTime(start)} – ${formatClockTime(end)}${days > 0 ? ` ${dayOffsetLabel(days)}` : ''}`
}

type SummaryEvent = Pick<InquiryEvent, 'eventDate' | 'eventLength' | 'eventTimezone' | 'eventGuests' | 'eventLocation'>

/** Most dates the collapsed header lists for a multi-event job before "+N more". */
const SUMMARY_MAX_DATES = 4

/**
 * The collapsed Events card's header items. Missing parts are omitted; only
 * the city truncates. Empty for no events.
 *
 * - One event: its date, start – end time when dated, guests, and the city of
 *   its location (falling back to `fallbackLocation`).
 * - Two or more: only their dates, chronological with TBD-dated events last
 *   (the order the list renders in), at most {@link SUMMARY_MAX_DATES} then
 *   "+N more". Times, guests and places don't summarize across events.
 */
function jobEventsSummaryItems(events: SummaryEvent[], fallbackLocation?: string): CardSummaryItem[] {
  const sorted = [...events].sort((a, b) => {
    if (!a.eventDate && !b.eventDate) return 0
    if (!a.eventDate) return 1
    if (!b.eventDate) return -1
    return Number(a.eventDate.seconds) - Number(b.eventDate.seconds)
  })
  const dayText = (event: SummaryEvent) =>
    event.eventDate ? formatShortDate(event.eventDate, event.eventTimezone || undefined) : 'Date TBD'

  if (sorted.length === 0) return []
  if (sorted.length > 1) {
    const shown = sorted.slice(0, SUMMARY_MAX_DATES).map(dayText).join(' · ')
    const extra = sorted.length - SUMMARY_MAX_DATES
    return [{ key: 'dates', icon: CalendarDays, label: `${shown}${extra > 0 ? ` +${extra} more` : ''}` }]
  }

  const [only] = sorted
  const items: CardSummaryItem[] = [{ key: 'date', icon: CalendarDays, label: dayText(only) }]
  const time = formatStartEndText(only)
  if (time) items.push({ key: 'time', icon: Clock, label: time })
  if (only.eventGuests) items.push({ key: 'guests', icon: Users, label: String(only.eventGuests), srLabel: 'guests' })
  const location = only.eventLocation?.trim() || fallbackLocation?.trim() || ''
  const locationLines = location ? splitAddressLines(location) : []
  if (locationLines.length > 0) {
    items.push({ key: 'location', icon: MapPin, label: locationLines[locationLines.length - 1], truncate: true })
  }
  return items
}

function ConflictCell({ conflict }: { conflict: ConflictInfo }) {
  if (!conflict.hasConflict) return null

  const tooltipText = [
    conflict.conflictingEvents.length > 0 &&
      `${conflict.conflictingEvents.length} booked event${conflict.conflictingEvents.length > 1 ? 's' : ''}`,
    conflict.conflictingInquiries.length > 0 &&
      `${conflict.conflictingInquiries.length} overlapping lead${conflict.conflictingInquiries.length > 1 ? 's' : ''}`,
  ]
    .filter(Boolean)
    .join(', ')

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex items-center">
          <AlertTriangle className="h-4 w-4 text-status-warning-fg" />
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <p>Scheduling conflict: {tooltipText}</p>
      </TooltipContent>
    </Tooltip>
  )
}


/** Batched save state of one event: a batch is pending or in flight, or just landed. */
type EventSaveState = 'saving' | 'saved'

interface EventBlockActions {
  /** Shows a field change at once and adds it to the event's debounced batch. */
  queueField: (eventId: string, maskPath: string, overridePatch: Partial<InquiryEvent>, mutationParams: Record<string, unknown>) => void
  saveDate: (event: InquiryEvent, date: Date | undefined, time: string) => void
  /** Queues a location change and announces a timezone the save moved the event into. */
  saveLocation: (event: InquiryEvent, location: string) => void
  onDeleteClick: (event: InquiryEvent) => void
  canDelete: boolean
}

function FieldLabel({ children }: { children: ReactNode }) {
  return <div className={cn(FIELD_LABEL_CLASS, 'mb-1')}>{children}</div>
}

const DATE_TILE_CLASS = 'flex w-20 shrink-0 flex-col items-center justify-center rounded-md border px-2 py-2 text-center'

/** Month / day / weekday, or "Date TBD": the face of both the picker tile and the read tile. */
function DateTileFace({ date }: { date: Date | undefined }) {
  return date ? (
    <>
      <span className="text-xs font-medium uppercase tracking-wider text-primary">{format(date, 'MMM')}</span>
      <span className="text-2xl font-medium leading-tight">{format(date, 'd')}</span>
      <span className="text-xs text-muted-foreground">{format(date, 'EEE')}</span>
    </>
  ) : (
    <>
      <CalendarIcon className="h-4 w-4" />
      <span className="text-xs">Date TBD</span>
    </>
  )
}

/** Date tile: month / day / weekday. The tile itself is the date picker's trigger. */
function DateTile({ event, disabled, onCommit }: DateTimeCellProps) {
  const date = timestampToDate(event.eventDate)
  const time = formatTimestampToTimeString(event.eventDate) || '12:00'
  return (
    <DatePicker
      value={date}
      onChange={(next) => onCommit(next, time)}
      disabled={disabled}
      trigger={
        <button
          type="button"
          disabled={disabled}
          aria-label={date ? format(date, 'MMM d, yyyy') : 'Date TBD'}
          className={cn(
            DATE_TILE_CLASS,
            'outline-none transition-colors',
            'hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring/50 disabled:cursor-default disabled:opacity-60 disabled:hover:bg-transparent',
            !date && 'min-h-[4.5rem] gap-1 border-dashed text-muted-foreground',
          )}
        >
          <DateTileFace date={date} />
        </button>
      }
    />
  )
}

/** The date tile of a locked (read-first) event: same face, not a picker. */
function ReadDateTile({ event }: { event: InquiryEvent }) {
  const date = timestampToDate(event.eventDate)
  return (
    <div
      title={date ? format(date, 'MMM d, yyyy') : 'Date TBD'}
      className={cn(DATE_TILE_CLASS, !date && 'min-h-[4.5rem] gap-1 border-dashed text-muted-foreground')}
    >
      <DateTileFace date={date} />
    </div>
  )
}

/**
 * A locked event's time range: "9:00 – 11:00 AM" when both ends share a
 * meridiem on the same day, otherwise "11:00 AM – 1:00 PM"; just the start
 * without a length. `days` is how many calendar days later the end falls.
 */
function formatReadTimeRange(event: Pick<InquiryEvent, 'eventDate' | 'eventLength'>): { text: string; days: number } | undefined {
  const start = timestampToDate(event.eventDate)
  if (!start) return undefined
  const startText = formatClockTime(start)
  const end = eventEndDate(event)
  if (!end) return { text: startText, days: 0 }
  const endText = formatClockTime(end)
  const days = differenceInCalendarDays(end, start)
  const meridiem = (text: string) => text.slice(-2)
  if (days === 0 && meridiem(startText) === meridiem(endText)) {
    return { text: `${startText.slice(0, -3)} – ${endText}`, days }
  }
  return { text: `${startText} – ${endText}`, days }
}

/** "2 hrs · 25 guests": the length and guests of a locked event, or undefined with neither. */
function readLengthGuestsLine(event: Pick<InquiryEvent, 'eventLength' | 'eventGuests'>): string | undefined {
  const parts = [
    event.eventLength ? hoursText(event.eventLength) : undefined,
    event.eventGuests ? plural(event.eventGuests, 'guest') : undefined,
  ]
  return parts.filter(Boolean).join(' · ') || undefined
}

/** "1 barista · 2 carts": the staffing of a locked event, or undefined with neither. */
function readStaffingLine(event: Pick<InquiryEvent, 'baristaCount' | 'cartCount'>): string | undefined {
  const parts = [
    event.baristaCount ? plural(event.baristaCount, 'barista') : undefined,
    event.cartCount ? plural(event.cartCount, 'cart') : undefined,
  ]
  return parts.filter(Boolean).join(' · ') || undefined
}

/**
 * A locked (read-first) event as one compact, label-free row: the time range
 * over the location, then the length and guests over the staffing. The two
 * columns stack below `sm`.
 */
function ReadEventDetails({ event }: { event: InquiryEvent }) {
  const range = formatReadTimeRange(event)
  const lengthGuests = readLengthGuestsLine(event)
  const staffing = readStaffingLine(event)
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:gap-6">
      {/* Capped so the length/staffing column follows the time and place
          instead of drifting to the far edge of a wide card. */}
      <div className="min-w-0 flex-1 sm:max-w-md">
        <p className="text-sm font-medium">
          {range ? (
            <>
              <span>{range.text}</span>
              <DayOffsetMarker days={range.days} />
            </>
          ) : (
            <span className="text-muted-foreground">Time TBD</span>
          )}
        </p>
        {event.eventLocation && (
          <p title={event.eventLocation} className="truncate text-sm text-muted-foreground">
            {event.eventLocation}
          </p>
        )}
      </div>
      {(lengthGuests || staffing) && (
        <div className="min-w-0 sm:shrink-0">
          {lengthGuests && <p className="text-sm">{lengthGuests}</p>}
          {staffing && <p className="text-sm text-muted-foreground">{staffing}</p>}
        </div>
      )}
    </div>
  )
}

/**
 * The event's one save indicator: a spinner while a batch is pending or in
 * flight (held briefly so a fast save doesn't flicker), then a check that
 * fades once the save settles. Fixed size so nothing shifts as it swaps.
 */
function EventSaveStatus({ status }: { status: EventSaveState | undefined }) {
  const prefersReducedMotion = useReducedMotion()
  const showSpinner = usePendingWithMinimumVisibleDuration(status === 'saving')
  const showSaved = !showSpinner && status === 'saved'
  const swap = {
    className: 'absolute inset-0 flex items-center justify-center',
    initial: prefersReducedMotion ? false : { opacity: 0, scale: 0.7 },
    animate: { opacity: 1, scale: 1 },
    exit: prefersReducedMotion ? undefined : { opacity: 0, scale: 0.7 },
    transition: prefersReducedMotion ? { duration: 0 } : ICON_SWAP_TRANSITION,
  }
  return (
    <span className="pointer-events-none relative flex h-8 w-8 shrink-0 items-center justify-center">
      <AnimatePresence initial={false}>
        {showSpinner ? (
          <motion.span key="saving" {...swap}>
            <Loader2
              role="status"
              aria-label="Saving event"
              className="h-3.5 w-3.5 animate-spin text-muted-foreground motion-reduce:animate-none"
            />
          </motion.span>
        ) : showSaved ? (
          <motion.span key="saved" {...swap}>
            <Check role="status" aria-label="Event saved" className="h-3.5 w-3.5 text-muted-foreground" />
          </motion.span>
        ) : null}
      </AnimatePresence>
    </span>
  )
}

interface EventBlockProps {
  event: InquiryEvent
  conflict: ConflictInfo
  actions: EventBlockActions
  /** Quote phase: the block has a pencil / Done toggle. */
  readFirst: boolean
  /** Shows plain values instead of the editors. */
  locked: boolean
  onUnlock: () => void
  onDone: () => void
  saveStatus: EventSaveState | undefined
}

function EventBlock({ event, conflict, actions, readFirst, locked, onUnlock, onDone, saveStatus }: EventBlockProps) {
  const { queueField, saveDate, saveLocation, onDeleteClick, canDelete } = actions
  const hasDate = timestampToDate(event.eventDate) != null
  // Whole minutes, whether typed, stepped, or inferred from an End time.
  const saveLength = (next: number) => {
    const eventLength = roundToMinute(next)
    queueField(event.id, 'event_length', { eventLength }, { eventLength })
  }
  return (
    <div className={cn('flex gap-4 rounded-lg border p-4', locked && 'items-center')}>
      <div className={cn('flex min-w-0 flex-1 flex-col gap-4 sm:flex-row', locked && 'sm:items-center')}>
        <div className="relative w-20 shrink-0">
          {locked ? (
            <ReadDateTile event={event} />
          ) : (
            <DateTile event={event} onCommit={(date, time) => saveDate(event, date, time)} />
          )}
          {conflict.hasConflict && (
            <div className="absolute -right-2 -top-2 rounded-full bg-background p-0.5">
              <ConflictCell conflict={conflict} />
            </div>
          )}
        </div>
        {locked ? (
          <ReadEventDetails event={event} />
        ) : (
          <div className="flex min-w-0 flex-1 flex-col gap-3">
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              {hasDate ? (
                // Start – End takes the old Time + Duration footprint (7rem + 1.5rem gap + 9.5rem).
                <div className="w-[18rem] shrink-0">
                  <FieldLabel>Time</FieldLabel>
                  <StartEndCell
                    event={event}
                    onStartCommit={(date, time) => saveDate(event, date, time)}
                    onLengthCommit={saveLength}
                  />
                </div>
              ) : (
                // Date TBD: no start to anchor an end, so keep the disabled Time and the Duration stepper.
                <>
                  <div className="w-28 shrink-0">
                    <FieldLabel>Time</FieldLabel>
                    <TimeCell event={event} onCommit={(date, time) => saveDate(event, date, time)} />
                  </div>
                  <div className="w-[9.5rem] shrink-0">
                    <FieldLabel>Duration</FieldLabel>
                    <NumberCell
                      value={event.eventLength}
                      ariaLabel="Edit Duration"
                      min={MINUTE_IN_HOURS}
                      step={MINUTE_IN_HOURS}
                      stepBy={0.25}
                      valueClassName="min-w-[5.5rem]"
                      format={formatDuration}
                      onCommit={saveLength}
                    />
                  </div>
                </>
              )}
              <div className="w-28 shrink-0">
                <FieldLabel>Guests</FieldLabel>
                <NumberCell
                  value={event.eventGuests}
                  ariaLabel="Edit Guests"
                  stepBy={25}
                  valueClassName="min-w-[3rem]"
                  format={formatCount}
                  onCommit={(next) => queueField(event.id, 'event_guests', { eventGuests: next }, { eventGuests: next })}
                />
              </div>
              <div className="w-24 shrink-0">
                <FieldLabel>Baristas</FieldLabel>
                <NumberCell
                  value={event.baristaCount}
                  ariaLabel="Edit Baristas"
                  stepBy={1}
                  valueClassName="min-w-[2.5rem]"
                  format={formatCount}
                  onCommit={(next) => queueField(event.id, 'barista_count', { baristaCount: next }, { baristaCount: next })}
                />
              </div>
              <div className="w-24 shrink-0">
                <FieldLabel>Carts</FieldLabel>
                <NumberCell
                  value={event.cartCount}
                  ariaLabel="Edit Carts"
                  stepBy={1}
                  valueClassName="min-w-[2.5rem]"
                  format={formatCount}
                  onCommit={(next) => queueField(event.id, 'cart_count', { cartCount: next }, { cartCount: next })}
                />
              </div>
            </div>
            <div className="min-w-0">
              <FieldLabel>Location</FieldLabel>
              <LocationCell value={event.eventLocation ?? ''} onCommit={(next) => saveLocation(event, next)} />
              <p className="mt-1 text-xs text-muted-foreground">
                {event.eventTimezone
                  ? `Timezone: ${timezoneLabel(event.eventTimezone)}`
                  : 'Timezone not set. It is detected when a location is saved.'}
              </p>
            </div>
          </div>
        )}
      </div>
      {/* Action column, top to bottom: edit toggle (Quote phase), delete, save status. */}
      <div role="group" aria-label="Event actions" className="-my-1 -mr-1 flex shrink-0 flex-col items-center gap-1">
        {readFirst && (
          <Tooltip>
            <TooltipTrigger asChild>
              {locked ? (
                <Button variant="ghost" size="icon-sm" aria-label="Edit event" className="text-muted-foreground" onClick={onUnlock}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              ) : (
                <Button variant="ghost" size="icon-sm" aria-label="Done editing" className="text-muted-foreground" onClick={onDone}>
                  <Check className="h-3.5 w-3.5" />
                </Button>
              )}
            </TooltipTrigger>
            <TooltipContent>
              {locked ? "Edit this event's notes here; the quote keeps its own events" : 'Done editing'}
            </TooltipContent>
          </Tooltip>
        )}
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Delete event"
          className="text-muted-foreground hover:text-destructive"
          disabled={!canDelete}
          title={canDelete ? undefined : 'Cannot delete the last event'}
          onClick={() => onDeleteClick(event)}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
        <EventSaveStatus status={saveStatus} />
      </div>
    </div>
  )
}

/** Quiet period after an event's last field change before its batch is sent. */
const EVENT_SAVE_DEBOUNCE_MS = 800
/** How long the "Event saved" check stays before it fades. */
const EVENT_SAVED_VISIBLE_MS = 1500

type UpdateInquiryEventFn = ReturnType<typeof useUpdateInquiryEvent>['updateEvent']
type UpdateInquiryEventResult = Awaited<ReturnType<UpdateInquiryEventFn>>

interface PendingEventBatch {
  mask: Set<string>
  params: Record<string, unknown>
  patch: Partial<InquiryEvent>
  /** Run with the response once this batch saves, one per mask path (the latest change's wins). */
  onSaved: Map<string, (response: UpdateInquiryEventResult) => void>
}

/**
 * Per-event batched saves. A field change shows at once (an optimistic
 * override) and joins that event's pending batch; after
 * {@link EVENT_SAVE_DEBOUNCE_MS} with no further change to the event, one
 * `UpdateInquiryEvent` goes out with every changed path in its update_mask.
 * One request per event is in flight at a time: a change made meanwhile
 * queues into the next batch. A failed batch reverts its own optimistic
 * values (not newer queued ones) and toasts. Pending batches flush on unmount.
 */
function useEventSaveBatches(updateEvent: UpdateInquiryEventFn) {
  const [overrides, setOverrides] = useState<Record<string, Partial<InquiryEvent>>>({})
  const [statuses, setStatuses] = useState<Record<string, EventSaveState>>({})
  // Mutated in place, never reassigned, so effects can hold them directly.
  const pendingRef = useRef(new Map<string, PendingEventBatch>())
  const debounceTimersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const savedTimersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const inFlightRef = useRef(new Set<string>())
  // Events whose batch should go as soon as the in-flight request settles.
  const flushAfterRef = useRef(new Set<string>())
  const updateEventRef = useRef(updateEvent)
  updateEventRef.current = updateEvent

  function setStatus(eventId: string, status: EventSaveState | undefined) {
    setStatuses((prev) => {
      if (prev[eventId] === status) return prev
      const next = { ...prev }
      if (status) next[eventId] = status
      else delete next[eventId]
      return next
    })
  }

  function clearTimer(timers: Map<string, ReturnType<typeof setTimeout>>, eventId: string) {
    const timer = timers.get(eventId)
    if (timer) clearTimeout(timer)
    timers.delete(eventId)
  }

  async function send(eventId: string, batch: PendingEventBatch) {
    let failed = false
    let response: UpdateInquiryEventResult | undefined
    try {
      response = await updateEventRef.current({ id: eventId, updateMask: [...batch.mask], ...batch.params })
    } catch (error) {
      failed = true
      const queued = pendingRef.current.get(eventId)?.patch ?? {}
      setOverrides((prev) => {
        const current = { ...(prev[eventId] ?? {}) } as Record<string, unknown>
        for (const field of Object.keys(batch.patch)) {
          if (!(field in queued)) delete current[field]
        }
        const next = { ...prev }
        if (Object.keys(current).length === 0) delete next[eventId]
        else next[eventId] = current as Partial<InquiryEvent>
        return next
      })
      reportError(error, { feature: 'job_event_update', kind: 'mutation' })
      toast.error('Failed to update event', { description: getErrorDescription(error) })
    } finally {
      inFlightRef.current.delete(eventId)
    }
    if (response) for (const onSaved of batch.onSaved.values()) onSaved(response)

    if (flushAfterRef.current.delete(eventId)) {
      flush(eventId)
      return
    }
    // A newer change is still inside its debounce window; its timer sends it.
    if (pendingRef.current.has(eventId)) return
    if (failed) {
      setStatus(eventId, undefined)
      return
    }
    setStatus(eventId, 'saved')
    clearTimer(savedTimersRef.current, eventId)
    savedTimersRef.current.set(
      eventId,
      setTimeout(() => {
        savedTimersRef.current.delete(eventId)
        setStatuses((prev) => {
          if (prev[eventId] !== 'saved') return prev
          const next = { ...prev }
          delete next[eventId]
          return next
        })
      }, EVENT_SAVED_VISIBLE_MS),
    )
  }

  /** Sends the event's pending batch now (or right after its in-flight request). */
  function flush(eventId: string) {
    clearTimer(debounceTimersRef.current, eventId)
    if (inFlightRef.current.has(eventId)) {
      if (pendingRef.current.has(eventId)) flushAfterRef.current.add(eventId)
      return
    }
    const batch = pendingRef.current.get(eventId)
    if (!batch) return
    pendingRef.current.delete(eventId)
    inFlightRef.current.add(eventId)
    void send(eventId, batch)
  }

  function queueField(
    eventId: string,
    maskPath: string,
    overridePatch: Partial<InquiryEvent>,
    mutationParams: Record<string, unknown>,
    onSaved?: (response: UpdateInquiryEventResult) => void,
  ) {
    setOverrides((prev) => ({ ...prev, [eventId]: { ...prev[eventId], ...overridePatch } }))
    const batch = pendingRef.current.get(eventId) ?? { mask: new Set<string>(), params: {}, patch: {}, onSaved: new Map() }
    batch.mask.add(maskPath)
    Object.assign(batch.params, mutationParams)
    Object.assign(batch.patch, overridePatch)
    if (onSaved) batch.onSaved.set(maskPath, onSaved)
    else batch.onSaved.delete(maskPath)
    pendingRef.current.set(eventId, batch)
    clearTimer(savedTimersRef.current, eventId)
    setStatus(eventId, 'saving')
    clearTimer(debounceTimersRef.current, eventId)
    debounceTimersRef.current.set(
      eventId,
      setTimeout(() => {
        debounceTimersRef.current.delete(eventId)
        flush(eventId)
      }, EVENT_SAVE_DEBOUNCE_MS),
    )
  }

  /** Drops an event's unsent batch, e.g. once the event is being deleted. */
  function discard(eventId: string) {
    clearTimer(debounceTimersRef.current, eventId)
    pendingRef.current.delete(eventId)
    flushAfterRef.current.delete(eventId)
    if (!inFlightRef.current.has(eventId)) setStatus(eventId, undefined)
  }

  const flushRef = useRef(flush)
  flushRef.current = flush
  useEffect(() => {
    const pending = pendingRef.current
    const savedTimers = savedTimersRef.current
    return () => {
      for (const eventId of [...pending.keys()]) flushRef.current(eventId)
      for (const timer of savedTimers.values()) clearTimeout(timer)
      savedTimers.clear()
    }
  }, [])

  return { overrides, statuses, queueField, flush, discard }
}


/**
 * Events card on the pipeline job detail page.
 *
 * A booked job (events sourced from `events.events`) renders the same
 * read-only summary list it always has — `JobEventLite` carries no
 * barista/cart counts, so it can't fill the full editable field set.
 * An unbooked job (events sourced from the inquiry's inquiry events, or with
 * no events yet) renders one stacked date-tile block per event (chronological, TBD last),
 * with every field directly editable in place: click or Enter to edit, Enter
 * or blur to commit, Escape to cancel. Commits show at once and save in one
 * debounced `UpdateInquiryEvent` per event (see `useEventSaveBatches`), with
 * a single save indicator in the block's action column. "Add Event" creates a row immediately (seeded from the
 * first existing event) with no intermediate form. Delete is disabled once
 * only one event remains — the backend rejects deleting the last one.
 *
 * These are the customer's request and stay editable whether or not the job
 * has quotes; each quote's own events (the proposal) are shown, and compared
 * against this request, on the Quotes card. In the Quote phase (`readFirst`)
 * each event reads as plain text until its pencil unlocks it, since changes
 * for the client belong on the quote; a hint links the current quote.
 */
export function JobEvents({
  jobId,
  inquiryId,
  events,
  isLoading,
  inquiry,
  isInquiryLoading,
  defaultCollapsed = false,
  readFirst = false,
  quoteId,
}: JobEventsProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed)
  const [deletingEvent, setDeletingEvent] = useState<InquiryEvent | null>(null)
  // Quote phase: the events the user has unlocked for editing; the rest read as plain text.
  const [unlockedIds, setUnlockedIds] = useState<ReadonlySet<string>>(() => new Set())

  const { createEvent, isLoading: isCreating } = useCreateInquiryEvent()
  const { updateEvent } = useUpdateInquiryEvent(inquiryId ?? '')
  const { overrides, statuses, queueField, flush, discard } = useEventSaveBatches(updateEvent)
  const { deleteEvent, isLoading: isDeleting } = useDeleteInquiryEvent(inquiryId ?? '')
  const { overlappingInquiries, overlappingEvents } = useInquiryOverlaps(inquiryId)

  const isBooked = events.some((event) => event.source === JobEventSourceEnum.JOB_EVENT_SOURCE_EVENT)
  const canEdit = Boolean(inquiryId) && !isBooked
  const isReadFirst = readFirst && canEdit

  const rawInquiryEvents = inquiry?.events ?? []
  const inquiryEvents = rawInquiryEvents.map((event) => {
    const override = overrides[event.id]
    return override ? { ...event, ...override } : event
  })
  const canDelete = inquiryEvents.length > 1

  const showSkeleton = isLoading || (canEdit && isInquiryLoading)

  function setUnlocked(eventId: string, unlocked: boolean) {
    setUnlockedIds((prev) => {
      const next = new Set(prev)
      if (unlocked) next.add(eventId)
      else next.delete(eventId)
      return next
    })
  }

  // The backend re-resolves the timezone when a location changes without one
  // (these batches never send event_timezone), so once the batch carrying the
  // location saves, say so if it moved the event into a different zone.
  function saveLocation(event: InquiryEvent, eventLocation: string) {
    const previousTimezone = event.eventTimezone ?? ''
    queueField(event.id, 'event_location', { eventLocation }, { eventLocation }, (response) => {
      const timezone = response?.inquiryEvent?.eventTimezone
      if (timezone && timezone !== previousTimezone) {
        toast.success(`Timezone updated to ${timezoneLabel(timezone)} to match the new location`)
      }
    })
  }

  function saveDate(event: InquiryEvent, date: Date | undefined, time: string) {
    const eventDate = date ? dateToTimestamp(combineDateAndTime(date, time)) : undefined
    queueField(event.id, 'event_date', { eventDate }, { eventDate })
  }

  async function handleAddEvent() {
    if (!inquiryId) return
    const fallback = rawInquiryEvents[0]
    try {
      const response = await createEvent({
        inquiryId,
        eventLength: fallback?.eventLength || 0.5,
        eventGuests: fallback?.eventGuests || 0,
        baristaCount: fallback?.baristaCount || 1,
        cartCount: fallback?.cartCount || 1,
        eventType: inquiry?.eventTypeKey || fallback?.eventTypeKey || undefined,
        eventLocation: fallback?.eventLocation || undefined,
      })
      // In the Quote phase a new event opens unlocked so it can be filled in.
      const newEventId = response?.inquiryEvent?.id
      if (isReadFirst && newEventId) setUnlocked(newEventId, true)
      toast.success('Event added')
    } catch (error) {
      reportError(error, { feature: 'job_event_create', kind: 'mutation' })
      toast.error('Failed to add event', { description: getErrorDescription(error) })
    }
  }

  async function handleDeleteConfirm() {
    if (!deletingEvent) return
    // Its unsent changes would only fail against a deleted event.
    discard(deletingEvent.id)
    try {
      await deleteEvent(deletingEvent.id)
      toast.success('Event deleted')
      setDeletingEvent(null)
    } catch (error) {
      reportError(error, { feature: 'job_event_delete', kind: 'mutation' })
      toast.error('Failed to delete event', { description: getErrorDescription(error) })
    }
  }

  // Chronological, with TBD-dated events last.
  const sortedEvents = [...inquiryEvents].sort((a, b) => {
    if (!a.eventDate && !b.eventDate) return 0
    if (!a.eventDate) return 1
    if (!b.eventDate) return -1
    return Number(a.eventDate.seconds) - Number(b.eventDate.seconds)
  })
  // Collapsed header items, from the events the card lists — the requested
  // events (location falling back to the inquiry's), or the job's events once
  // booked: "Jun 14 · 8:16 PM – 11:46 PM · 120 guests · Cincinnati, OH" for
  // one event, "Jun 14 · Jun 15 · Date TBD" for several.
  const summaryItems = showSkeleton
    ? []
    : canEdit
      ? jobEventsSummaryItems(inquiryEvents, inquiry?.eventLocation)
      : jobEventsSummaryItems(events)
  // The header's count follows the list the card shows: the lead's editable
  // requested events, or the read-only job events once booked.
  const eventCount = canEdit ? inquiryEvents.length : events.length
  const actions: EventBlockActions = {
    queueField,
    saveDate,
    saveLocation,
    onDeleteClick: setDeletingEvent,
    canDelete,
  }

  return (
    <Card className="gap-0 overflow-hidden pt-0">
      {/* The whole header toggles the card. Its own controls (Add Event, the
          chevron) keep their clicks; the chevron stays as the
          keyboard-reachable toggle. */}
      <CardHeader
        className="cursor-pointer gap-0 py-3 select-none"
        onClick={(event) => {
          if ((event.target as Element).closest('button, a')) return
          setCollapsed((prev) => !prev)
        }}
      >
        {/* Collapsed, the card is this one row: the summary items sit beside
            the title and its count. */}
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
          <div className="flex min-w-0 flex-1 items-baseline gap-x-3">
            <CardTitle className="shrink-0 text-base">Events</CardTitle>
            {!showSkeleton && eventCount > 0 && (
              <span className="shrink-0 text-sm text-muted-foreground">
                {eventCount} {eventCount === 1 ? 'event' : 'events'}
              </span>
            )}
            <CardSummaryItems show={collapsed && summaryItems.length > 0} label="Events summary" items={summaryItems} />
          </div>
          {/* -my-1: the 32px Add Event button overhangs the text line instead of
              growing the header, so it matches the Original Inquiry header. */}
          <div className="-my-1 flex shrink-0 items-center gap-1">
            {canEdit && !collapsed && (
              <Button size="sm" onClick={handleAddEvent} disabled={isCreating}>
                <Plus className="h-4 w-4" />
                Add Event
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-expanded={!collapsed}
              aria-label={collapsed ? 'Show events' : 'Hide events'}
              onClick={() => setCollapsed((prev) => !prev)}
            >
              <CollapseChevron open={!collapsed} />
            </Button>
          </div>
        </div>
      </CardHeader>
      {/* The divider under the header belongs to the collapsing body, so it
          leaves with the body instead of jumping when the state flips. */}
      <AnimatedCollapse open={!collapsed}>
        <CardContent className="border-t px-0 pb-0">
          {showSkeleton ? (
            <div className="space-y-2 px-6 py-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : !canEdit ? (
            events.length === 0 ? (
              <p className="px-6 py-4 text-sm text-muted-foreground">No events yet.</p>
            ) : (
              <div className="divide-y">
                {events.map((event) => {
                  const pageParams =
                    jobId && event.source === JobEventSourceEnum.JOB_EVENT_SOURCE_EVENT
                      ? { id: jobId, eventId: event.id }
                      : undefined
                  const title = jobDetailEventTitle(event)
                  return (
                    <div key={event.id} className="flex items-center justify-between gap-3 px-6 py-3">
                      <div className="min-w-0">
                        {pageParams ? (
                          <Link
                            to="/pipeline/$id/events/$eventId"
                            params={pageParams}
                            className="text-sm font-medium hover:text-brand"
                          >
                            {title}
                          </Link>
                        ) : (
                          <div className="text-sm font-medium">{title}</div>
                        )}
                        <div className="text-xs text-muted-foreground">{jobDetailEventLine(event)}</div>
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        <Badge variant={jobDetailEventStageVariant(event)}>{jobDetailEventStageLabel(event)}</Badge>
                        {pageParams && (
                          <Link
                            to="/pipeline/$id/events/$eventId"
                            params={pageParams}
                            aria-label={`Open ${title} on ${jobDetailEventLine(event)}`}
                            className="text-xs text-muted-foreground hover:text-foreground"
                          >
                            Open →
                          </Link>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )
          ) : (
            <>
              {isReadFirst && (
                <p className="px-6 pt-4 text-xs text-muted-foreground">
                  Quoted — event changes for the client go on the quote.
                  {jobId && quoteId && (
                    <>
                      {' '}
                      <Link
                        to="/pipeline/$id/quotes/$quoteId"
                        params={{ id: jobId, quoteId }}
                        className="font-medium text-foreground underline-offset-4 hover:underline"
                      >
                        Open quote
                      </Link>
                    </>
                  )}
                </p>
              )}
              {inquiryEvents.length === 0 ? (
                <p className={cn('px-6 text-sm text-muted-foreground', isReadFirst ? 'pb-4 pt-3' : 'py-4')}>No events yet.</p>
              ) : (
                <div className={cn('flex flex-col gap-3 px-6', isReadFirst ? 'pb-4 pt-3' : 'py-4')}>
                  {sortedEvents.map((event) => (
                    <EventBlock
                      key={event.id}
                      event={event}
                      conflict={getEventConflicts(event, overlappingInquiries, overlappingEvents)}
                      actions={actions}
                      readFirst={isReadFirst}
                      locked={isReadFirst && !unlockedIds.has(event.id)}
                      onUnlock={() => setUnlocked(event.id, true)}
                      onDone={() => {
                        flush(event.id)
                        setUnlocked(event.id, false)
                      }}
                      saveStatus={statuses[event.id]}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </CardContent>
      </AnimatedCollapse>

      <AlertDialog open={deletingEvent != null} onOpenChange={(open) => !open && setDeletingEvent(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center">
              <AlertTriangle className="mr-2 h-5 w-5 text-destructive" />
              Delete Event
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this event? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} disabled={isDeleting} variant="destructive">
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
